import { Link, Navigate, useNavigate } from 'react-router-dom'
import { FiAlertCircle, FiCpu, FiRefreshCw, FiSave, FiTrash2, FiUser } from 'react-icons/fi'
import toast from 'react-hot-toast'
import { useEffect, useMemo, useState } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { useUserRealtimeData } from '../hooks/useUserRealtimeData'
import { formatDeviceAssociationDate, useUserDevices } from '../hooks/useUserDevices'
import Card from '../components/ui/Card'
import Button from '../components/ui/Button'
import Select from '../components/ui/Select'
import CancelSubscriptionModal from '../components/CancelSubscriptionModal'
import PaymentMethodBlock from '../components/PaymentMethodBlock'
import { asaasStatusPtbr } from '../utils/asaasStatusPtbr'
import { planDisplayName, formatPlanCurrency, getMonthlyAmount, normalizePlanId } from '../utils/subscriptionPlan'
import { apiUrl } from '../config/api'
import { getProfileDisplayName, getProfilePhone } from '../utils/userProfile'
import { buildPaymentMethodFromPlan } from '../utils/billingType'
import { getAuth } from 'firebase/auth'
import { LEGAL, privacyMailto, PRIVACY_DELETE_SUBJECT } from '../config/legal'
const PLAN_OPTIONS = [
  { value: 'basico', label: `Básico — ${formatPlanCurrency(getMonthlyAmount('basico'))}/mês` },
  { value: 'pro', label: `Pro — ${formatPlanCurrency(getMonthlyAmount('pro'))}/mês` },
]

const AccountPage = () => {
  const navigate = useNavigate()
  const { user, loading: authLoading, updateMyProfile } = useAuth()
  const { profile, plan } = useUserRealtimeData()
  const { devices, loading: devicesLoading } = useUserDevices()

  const initial = useMemo(
    () => ({
      displayName: getProfileDisplayName(profile, user?.displayName),
      phone: getProfilePhone(profile),
    }),
    [user?.displayName, profile]
  )

  const [form, setForm] = useState(initial)
  const [saving, setSaving] = useState(false)
  const [desiredPlan, setDesiredPlan] = useState('basico')
  const [changingPlan, setChangingPlan] = useState(false)
  const [showCancelModal, setShowCancelModal] = useState(false)
  const [syncing, setSyncing] = useState(false)
  const [photoError, setPhotoError] = useState(false)

  useEffect(() => {
    setForm(initial)
  }, [initial])

  useEffect(() => {
    const id = normalizePlanId(plan?.planId)
    if (id === 'basico' || id === 'pro') setDesiredPlan(id)
  }, [plan?.planId])

  useEffect(() => {
    let alive = true
    ;(async () => {
      try {
        setSyncing(true)
        const token = await getAuth().currentUser?.getIdToken()
        if (!token) return

        try {
          await fetch(apiUrl('/api/device/sync'), {
            headers: { Authorization: `Bearer ${token}` },
          })
        } catch (e) {
          // silencioso
        }

        const resp = await fetch(apiUrl('/api/asaas/my-plan'), {
          headers: { Authorization: `Bearer ${token}` },
        })
        const raw = await resp.text()
        const data = (() => {
          try { return JSON.parse(raw) } catch { return null }
        })()
        if (!alive) return
        if (!data) return
        if (!resp.ok || !data.ok) return
      } catch (e) {
        // silencioso para não poluir UX
      } finally {
        if (alive) setSyncing(false)
      }
    })()
    return () => { alive = false }
  }, [])

  if (authLoading) return null
  if (!user) return <Navigate to="/" replace />

  const planLabel = plan?.planName || planDisplayName(plan?.planId)

  const isFreePlan = normalizePlanId(plan?.planId) === 'gratuito'
  const statusLabel = isFreePlan ? null : asaasStatusPtbr(plan?.status) || null
  const photoUrl = user.photoURL || profile?.photoURL || null
  const nextDueDateIso = plan?.nextDueDate || null
  const accessUntilIso = plan?.accessExpiresAt || nextDueDateIso
  const formatDateIso = (iso) =>
    iso
      ? new Intl.DateTimeFormat('pt-BR', { timeZone: 'UTC' }).format(new Date(`${iso}T00:00:00Z`))
      : '—'
  const nextDueDateLabel = formatDateIso(nextDueDateIso)
  const accessUntilLabel = formatDateIso(accessUntilIso)
  const todayIso = new Date().toISOString().slice(0, 10)

  const isSubscriptionActive = String(plan?.status || '').toUpperCase() === 'ACTIVE'
  const isPaidActivePlan =
    plan?.planActivated === true &&
    normalizePlanId(plan?.planId) &&
    normalizePlanId(plan?.planId) !== 'gratuito'
  const isCancelledWithAccess =
    Boolean(plan?.cancelledAt) &&
    Boolean(accessUntilIso) &&
    accessUntilIso > todayIso &&
    isPaidActivePlan
  const hasLiveSubscription = isSubscriptionActive && !plan?.cancelledAt
  const isAnnualPix =
    plan?.billingCycle === 'ANNUAL' &&
    String(plan?.billingType || '').toUpperCase() === 'PIX' &&
    !plan?.asaasSubscriptionId
  const paymentMethod = buildPaymentMethodFromPlan(plan)
  const canChangePlan = hasLiveSubscription && !isAnnualPix
  const isSamePlanSelected =
    normalizePlanId(desiredPlan) === normalizePlanId(plan?.planId)
  const canReactivate = isCancelledWithAccess
  const canCancelSubscription = isPaidActivePlan && !plan?.cancelledAt
  const showBillingDetails = isPaidActivePlan

  return (
    <main className="w-full min-w-0 pt-24 sm:pt-28 pb-16">
      <div className="container-custom">
        <div className="mb-8">
          <p className="text-xs font-bold text-accent uppercase tracking-[0.2em] mb-2">Conta</p>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-gray-900 dark:text-white tracking-tight">
            Minha conta
          </h1>
          <p className="text-sm sm:text-base text-gray-600 dark:text-gray-300 mt-2">
            Veja seu plano e atualize seus dados.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-1 space-y-4">
            <Card padding="lg" hover={false}>
              <p className="text-xs text-gray-500 dark:text-gray-400 font-bold uppercase tracking-[0.2em]">Perfil</p>
              <div className="mt-3 flex items-center gap-3">
                <div className="h-12 w-12 rounded-2xl bg-accent/10 dark:bg-accent/20 overflow-hidden flex items-center justify-center flex-shrink-0">
                  {photoUrl && !photoError ? (
                    <img
                      src={photoUrl}
                      alt=""
                      className="h-full w-full object-cover"
                      referrerPolicy="no-referrer"
                      onError={() => setPhotoError(true)}
                    />
                  ) : (
                    <FiUser className="w-6 h-6 text-accent" />
                  )}
                </div>
                <div className="min-w-0">
                  <p className="text-base font-extrabold text-gray-900 dark:text-white truncate">
                    {user.displayName || profile?.displayName || 'Minha conta'}
                  </p>
                  <p className="text-xs text-gray-600 dark:text-gray-400 truncate">{user.email}</p>
                </div>
              </div>
            </Card>

            <Card padding="lg" hover={false}>
              <p className="text-xs text-gray-500 dark:text-gray-400 font-bold uppercase tracking-[0.2em]">Plano</p>
              <p className="mt-2 text-lg font-extrabold text-gray-900 dark:text-white">{planLabel || '—'}</p>
              {!isFreePlan && statusLabel && (
                <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
                  Status: <span className="font-semibold">{statusLabel}</span>
                </p>
              )}
              {showBillingDetails && plan?.billingCycle === 'ANNUAL' && plan?.autoRenew !== false && (
                <p className="mt-1 text-xs text-accent font-semibold">
                  Plano anual · renovação automática no cartão
                </p>
              )}
              {showBillingDetails && plan?.billingCycle === 'ANNUAL' && plan?.autoRenew === false && (
                <p className="mt-1 text-xs text-amber-600 dark:text-amber-400 font-semibold">
                  Plano anual via PIX · renovação manual necessária
                </p>
              )}
              {showBillingDetails && (
                <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
                  {isCancelledWithAccess || (plan?.autoRenew === false && plan?.accessExpiresAt)
                    ? (
                      <>
                        Acesso até:{' '}
                        <span className="font-semibold">
                          {formatDateIso(plan?.accessExpiresAt || accessUntilIso)}
                        </span>
                      </>
                    )
                    : (
                      <>
                        Próxima cobrança: <span className="font-semibold">{nextDueDateLabel}</span>
                      </>
                    )}
                </p>
              )}
              {isCancelledWithAccess && (
                <p className="mt-2 text-xs text-amber-700 dark:text-amber-300 font-medium leading-relaxed">
                  Renovação cancelada. Para voltar a renovar, use o checkout — não é possível reativar por aqui com &quot;Atualizar plano&quot;.
                </p>
              )}
              {isFreePlan && (
                <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
                  Recursos limitados. Assine um plano pago para desbloquear mais dispositivos e funcionalidades.
                </p>
              )}
            </Card>

            {showBillingDetails && paymentMethod?.billingType && (
              <Card padding="lg" hover={false}>
                <p className="text-xs text-gray-500 dark:text-gray-400 font-bold uppercase tracking-[0.2em]">
                  Método de pagamento
                </p>
                <div className="mt-3">
                  <PaymentMethodBlock
                    variant="sidebar"
                    paymentMethod={paymentMethod}
                    cancelled={canReactivate}
                    canUpdateCard={paymentMethod?.canUpdateCard}
                    onUpdateCard={() => navigate('/account/update-card')}
                  />
                </div>
              </Card>
            )}

            <Card padding="lg" hover={false}>
              <p className="text-xs text-gray-500 dark:text-gray-400 font-bold uppercase tracking-[0.2em]">E-mail</p>
              <p className="mt-2 text-sm font-extrabold text-gray-900 dark:text-white break-all">{user.email}</p>
            </Card>

            <Card padding="lg" hover={false}>
              <div className="flex items-center justify-between gap-2">
                <p className="text-xs text-gray-500 dark:text-gray-400 font-bold uppercase tracking-[0.2em]">
                  Dispositivos
                </p>
                {!devicesLoading && devices.length > 0 && (
                  <span className="text-[11px] font-semibold text-gray-500 dark:text-gray-400">
                    {devices.length}
                  </span>
                )}
              </div>

              <div className="mt-3">
                {devicesLoading ? (
                  <p className="text-sm text-gray-500 dark:text-gray-400">Carregando dispositivos...</p>
                ) : devices.length === 0 ? (
                  <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
                    Nenhum dispositivo vinculado a esta conta.
                  </p>
                ) : (
                  <ul className={devices.length > 1 ? 'space-y-3' : ''}>
                    {devices.map((device) => {
                      const assocLabel = formatDeviceAssociationDate(device.associationDate)
                      const content = (
                        <div className="flex items-start gap-3">
                          <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-accent/10 dark:bg-accent/20 text-accent">
                            <FiCpu className="w-5 h-5" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-extrabold text-gray-900 dark:text-white truncate">
                              {device.name}
                            </p>
                            <p className="mt-1 font-mono text-sm tracking-wide text-gray-700 dark:text-gray-200 truncate">
                              {device.id}
                            </p>
                            <p className="mt-1 text-xs text-gray-600 dark:text-gray-400 leading-relaxed">
                              {device.isOwner ? 'Proprietário' : 'Convidado'}
                              {assocLabel ? ` · vinculado em ${assocLabel}` : ''}
                            </p>
                          </div>
                        </div>
                      )

                      return (
                        <li
                          key={device.id}
                          className={
                            devices.length > 1
                              ? 'rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50/80 dark:bg-gray-800/50 p-3'
                              : undefined
                          }
                        >
                          {content}
                        </li>
                      )
                    })}
                  </ul>
                )}
              </div>
            </Card>
          </div>

          <div className="lg:col-span-2 space-y-6">
            <Card padding="lg" hover={false}>
              <h2 className="text-lg font-extrabold text-gray-900 dark:text-white mb-2">Assinatura</h2>
              {canReactivate ? (
                <p className="text-sm text-gray-600 dark:text-gray-300 mb-5 leading-relaxed">
                  Sua renovação automática está <strong>cancelada</strong>. Você mantém o acesso ao plano pago até o fim do
                  ciclo já pago; depois disso o plano volta ao <strong>Gratuito</strong>.
                </p>
              ) : (
                <p className="text-sm text-gray-600 dark:text-gray-300 mb-5">
                  Troque de plano ou cancele a renovação automática. Ao cancelar, você{' '}
                  <strong>mantém o acesso ao plano pago até o fim do ciclo já pago</strong>, novas cobranças
                  são encerradas e o cartão é removido da central de pagamentos por segurança. Depois dessa data, o plano
                  volta ao <strong>Gratuito</strong>.
                </p>
              )}
              {canReactivate && (
                <div className="mb-5 rounded-2xl border-2 border-amber-400/70 dark:border-amber-500/50 bg-amber-50/90 dark:bg-amber-500/10 p-4 sm:p-5">
                  <div className="flex gap-3">
                    <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-amber-200/80 dark:bg-amber-500/25 text-amber-800 dark:text-amber-200">
                      <FiAlertCircle className="w-5 h-5" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-extrabold text-amber-950 dark:text-amber-50">
                        Renovação cancelada — reativação somente pelo checkout
                      </p>
                      <p className="mt-1 text-sm text-amber-900/90 dark:text-amber-100/90 leading-relaxed">
                        Acesso ao <strong>{planLabel}</strong> até{' '}
                        <strong>{accessUntilLabel}</strong>. Não haverá novas cobranças e o cartão foi removido da central de pagamentos.
                      </p>
                      <ul className="mt-3 space-y-2 text-sm text-amber-900/85 dark:text-amber-100/85 leading-relaxed">
                        <li className="flex gap-2">
                          <span className="text-amber-600 dark:text-amber-300 font-bold shrink-0">1.</span>
                          <span>Escolha o plano que deseja manter abaixo.</span>
                        </li>
                        <li className="flex gap-2">
                          <span className="text-amber-600 dark:text-amber-300 font-bold shrink-0">2.</span>
                          <span>
                            Clique em <strong>Ir para o checkout e reativar</strong> e cadastre um{' '}
                            <strong>novo cartão</strong>.
                          </span>
                        </li>
                        <li className="flex gap-2">
                          <span className="text-amber-600 dark:text-amber-300 font-bold shrink-0">!</span>
                          <span>
                            <strong>Não use &quot;Atualizar plano&quot;</strong> — essa opção fica indisponível com a
                            renovação cancelada. Trocar de plano ou voltar a renovar automaticamente só pelo checkout.
                          </span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              )}
              {syncing && (
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
                  Sincronizando dados da assinatura...
                </p>
              )}
              {plan?.pendingPlanId && !plan?.planActivated && (
                <div className="mb-5 rounded-2xl border border-amber-300/60 dark:border-amber-500/40 bg-amber-50/60 dark:bg-amber-500/10 p-4">
                  <p className="text-sm font-semibold text-amber-800 dark:text-amber-200">
                    Pagamento pendente do plano{' '}
                    <span className="font-extrabold">{plan.pendingPlanName || planDisplayName(plan.pendingPlanId)}</span>.
                    Seu acesso permanece no plano <span className="font-extrabold">{planLabel || 'atual'}</span> até a confirmação.
                  </p>
                </div>
              )}
              {!isFreePlan && !hasLiveSubscription && !canReactivate && plan?.status && !plan?.pendingPlanId && (
                <div className="mb-5 rounded-2xl border border-amber-300/60 dark:border-amber-500/40 bg-amber-50/60 dark:bg-amber-500/10 p-4">
                  <p className="text-sm font-semibold text-amber-800 dark:text-amber-200">
                    Sua assinatura está com status <span className="font-extrabold">{statusLabel}</span>. Alterações de plano e cancelamento ficam disponíveis apenas quando estiver <span className="font-extrabold">Ativa</span>.
                  </p>
                </div>
              )}

              {isFreePlan ? (
                <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-gray-50/80 dark:bg-gray-800/50 p-4 mb-5">
                  <p className="text-sm text-gray-700 dark:text-gray-300">
                    Você está no plano Gratuito. Escolha um plano na página de preços para assinar.
                  </p>
                  <Button
                    type="button"
                    variant="primary"
                    size="md"
                    className="mt-4"
                    onClick={() => { window.location.href = '/#pricing' }}
                  >
                    Ver planos
                  </Button>
                </div>
              ) : (
              <>
              {isAnnualPix && (
                <div className="mb-5 rounded-2xl border border-amber-300/60 dark:border-amber-500/40 bg-amber-50/60 dark:bg-amber-500/10 p-4">
                  <p className="text-sm text-amber-800 dark:text-amber-200 leading-relaxed">
                    Plano anual via PIX: para trocar de plano ou renovar, use o{' '}
                    <button
                      type="button"
                      className="font-semibold text-accent hover:underline"
                      onClick={() => navigate(`/checkout/${normalizePlanId(plan?.planId) || 'basico'}?cycle=annual`)}
                    >
                      checkout
                    </button>
                    .
                  </p>
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="desired-plan" className="form-label">
                    {canReactivate ? 'Plano na reativação' : 'Trocar para'}
                  </label>
                  <Select
                    id="desired-plan"
                    options={PLAN_OPTIONS}
                    value={desiredPlan}
                    onChange={setDesiredPlan}
                    placeholder="Selecione o plano"
                    compact
                    aria-label={canReactivate ? 'Plano na reativação' : 'Trocar para'}
                  />
                </div>
              </div>

              {canReactivate && (
                <p className="mt-4 text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                  O botão abaixo abre o checkout com o plano selecionado. Lá você confirma os dados e cadastra o cartão
                  para voltar a renovar automaticamente. Seu acesso atual continua até{' '}
                  <strong className="text-gray-900 dark:text-white">{accessUntilLabel}</strong>.
                </p>
              )}

              {!canReactivate && !canChangePlan && isPaidActivePlan && plan?.cancelledAt && (
                <p className="mt-4 text-sm text-amber-700 dark:text-amber-300 leading-relaxed">
                  A renovação está cancelada. Para voltar a assinar ou trocar de plano, use o checkout — a opção
                  &quot;Atualizar plano&quot; não está disponível neste estado.
                </p>
              )}

              {canChangePlan && paymentMethod?.billingType === 'CREDIT_CARD' && (
                <p className="mt-4 text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
                  A troca de plano altera o valor das próximas mensalidades. O cartão cadastrado continua o mesmo.
                </p>
              )}

              <div className="flex flex-col sm:flex-row gap-3 mt-5">
                {canReactivate ? (
                  <Button
                    type="button"
                    variant="primary"
                    size="md"
                    className="w-full sm:w-auto gap-2"
                    onClick={() => navigate(`/checkout/${desiredPlan}`)}
                  >
                    <FiRefreshCw className="w-5 h-5" />
                    Ir para o checkout e reativar
                  </Button>
                ) : (
                  <>
                  <Button
                    type="button"
                    variant="primary"
                    size="md"
                    className="w-full sm:w-auto gap-2"
                    disabled={changingPlan || !canChangePlan || isSamePlanSelected}
                    onClick={async () => {
                      try {
                        setChangingPlan(true)
                        const token = await getAuth().currentUser?.getIdToken()
                        if (!token) throw new Error('Você precisa estar logado.')

                        const resp = await fetch(apiUrl('/api/asaas/change-plan'), {
                          method: 'POST',
                          headers: {
                            'Content-Type': 'application/json',
                            Authorization: `Bearer ${token}`,
                          },
                          body: JSON.stringify({ planId: desiredPlan }),
                        })
                        const raw = await resp.text()
                        const data = (() => {
                          try { return JSON.parse(raw) } catch { return null }
                        })()
                        if (!data) {
                          throw new Error(`Resposta inválida do backend (não-JSON). Verifique se o backend está rodando em :3000. Início: ${raw.slice(0, 60)}`)
                        }
                        if (!resp.ok || !data.ok) {
                          const message = data.error || 'Erro ao trocar plano.'
                          if (resp.status === 409 && /cancelad/i.test(message)) {
                            throw new Error(
                              `${message} Use o botão "Ir para o checkout e reativar" para cadastrar um novo cartão.`
                            )
                          }
                          throw new Error(message)
                        }

                        toast.success('Plano atualizado! A alteração vale para as próximas mensalidades.')
                      } catch (err) {
                        console.error(err)
                        toast.error(err.message || 'Erro ao trocar plano.')
                      } finally {
                        setChangingPlan(false)
                      }
                    }}
                  >
                    Atualizar plano
                  </Button>
                  {!canChangePlan && isPaidActivePlan && !plan?.cancelledAt && !isAnnualPix && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 sm:self-center leading-relaxed">
                      Troca de plano disponível apenas com assinatura ativa e renovação automática ligada.
                    </p>
                  )}
                  </>
                )}

                {canCancelSubscription && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="md"
                    className="w-full sm:w-auto gap-2 text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors"
                    onClick={() => setShowCancelModal(true)}
                  >
                    <FiTrash2 className="w-5 h-5" />
                    Cancelar renovação
                  </Button>
                )}
              </div>
              </>
              )}
            </Card>

            <Card padding="lg" hover={false}>
              <h2 className="text-lg font-extrabold text-gray-900 dark:text-white mb-6">Dados do perfil</h2>

              <form
                className="space-y-4"
                onSubmit={async (e) => {
                  e.preventDefault()
                  try {
                    setSaving(true)
                    await updateMyProfile({ displayName: form.displayName, phone: form.phone })
                    toast.success('Perfil atualizado!')
                  } catch (err) {
                    console.error(err)
                    toast.error(err.message || 'Erro ao atualizar perfil.')
                  } finally {
                    setSaving(false)
                  }
                }}
              >
                <div>
                  <label className="form-label">Nome</label>
                  <div className="relative">
                    <FiUser className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 dark:text-gray-500 pointer-events-none" />
                    <input
                      className="input-field !pl-[3.25rem] sm:!pl-[3.5rem]"
                      value={form.displayName}
                      onChange={(e) => setForm({ ...form, displayName: e.target.value })}
                      placeholder="Seu nome"
                      autoComplete="name"
                    />
                  </div>
                </div>

                <div>
                  <label className="form-label">Telefone</label>
                  <input
                    className="input-field"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    placeholder="(00) 00000-0000"
                    autoComplete="tel"
                  />
                </div>

                <div className="flex flex-col sm:flex-row gap-3 pt-2">
                  <Button type="submit" variant="primary" size="md" className="w-full sm:w-auto gap-2" disabled={saving}>
                    {saving ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                        Salvando...
                      </>
                    ) : (
                      <>
                        <FiSave className="w-5 h-5" />
                        Salvar alterações
                      </>
                    )}
                  </Button>
                </div>
              </form>
            </Card>

            <Card padding="lg" hover={false} className="border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-extrabold text-gray-900 dark:text-white mb-2">Privacidade (LGPD)</h2>
              <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed mb-4">
                Você pode solicitar acesso, correção, portabilidade ou exclusão dos seus dados pessoais.
                Consulte a{' '}
                <Link to="/privacidade" className="text-accent font-semibold hover:underline">
                  Política de Privacidade
                </Link>{' '}
                para mais detalhes.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                <Button
                  type="button"
                  variant="secondary"
                  size="md"
                  className="w-full sm:w-auto"
                  onClick={() => {
                    window.location.href = privacyMailto(
                      'Solicitação LGPD — Acesso ou correção',
                      `Olá,\n\nSolicito informações sobre meus dados pessoais.\n\nE-mail da conta: ${user.email || ''}\n\nDescrição do pedido: \n`
                    )
                  }}
                >
                  Solicitar acesso ou correção
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="md"
                  className="w-full sm:w-auto gap-2 text-red-500 hover:text-red-600"
                  onClick={() => {
                    window.location.href = privacyMailto(
                      PRIVACY_DELETE_SUBJECT,
                      `Olá,\n\nSolicito a exclusão dos meus dados pessoais vinculados à conta.\n\nE-mail da conta: ${user.email || ''}\n\nEntendo que o encerramento pode cancelar o acesso ao serviço.\n`
                    )
                  }}
                >
                  <FiTrash2 className="w-5 h-5" />
                  Solicitar exclusão de dados
                </Button>
              </div>
            </Card>
          </div>
        </div>
      </div>

      {showCancelModal && (
        <CancelSubscriptionModal
          planName={planLabel}
          accessUntilLabel={accessUntilLabel}
          onClose={() => setShowCancelModal(false)}
        />
      )}
    </main>
  )
}

export default AccountPage

